import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';

interface User {
  id: number;
  username: string;
  role: string;
  created_at: string;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Администратор',
  operator: 'Оператор',
  monitoring: 'Мониторинг',
  accountant: 'Бухгалтер',
};

export function UserManagementPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<User | null>(null);
  const [formUsername, setFormUsername] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formRole, setFormRole] = useState('operator');
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.users.list();
      setUsers(data as any);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  function openCreate() {
    setEditTarget(null);
    setFormUsername('');
    setFormPassword('');
    setFormRole('operator');
    setShowModal(true);
  }

  function openEdit(user: User) {
    setEditTarget(user);
    setFormUsername(user.username);
    setFormPassword('');
    setFormRole(user.role);
    setShowModal(true);
  }

  async function handleSave() {
    if (!formUsername.trim() || !formRole) return;
    if (!editTarget && !formPassword.trim()) return;

    setSaving(true);
    try {
      if (editTarget) {
        await api.users.update(editTarget.id, {
          username: formUsername.trim(),
          ...(formPassword.trim() ? { password: formPassword.trim() } : {}),
          role: formRole,
        });
      } else {
        await api.users.create({
          username: formUsername.trim(),
          password: formPassword.trim(),
          role: formRole,
        });
      }
      setShowModal(false);
      await loadUsers();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    try {
      await api.users.delete(id);
      setDeleteTarget(null);
      await loadUsers();
    } catch (e: any) {
      setError(e.message);
    }
  }

  const inputStyle = 'w-full rounded-lg border border-ct-border bg-ct-surface px-4 py-2.5 text-sm text-ct-text placeholder:text-ct-text-muted/50 focus:ring-2 focus:ring-ct-primary focus:ring-offset-1 focus:border-ct-primary focus:outline-none transition-all duration-200';

  return (
    <div>
      <div className="mb-4 inline-flex items-center gap-3 rounded-full bg-indigo-50 border border-indigo-100 px-5 py-2">
        <span className="h-2 w-2 rounded-full bg-ct-primary" />
        <span className="text-xs font-bold uppercase tracking-wider text-ct-primary">Администрирование</span>
      </div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-extrabold tracking-tight text-ct-text">
          Управление{' '}
          <span className="bg-gradient-to-r from-ct-primary to-ct-secondary bg-clip-text text-transparent">
            пользователями
          </span>
        </h1>
        <button
          onClick={openCreate}
          className="rounded-full bg-gradient-to-r from-ct-primary to-ct-secondary px-6 py-2 text-sm font-bold text-ct-on-primary shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98]"
        >
          + Создать пользователя
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">{error}</div>
      )}

      {loading ? (
        <p className="text-ct-text-muted">Загрузка...</p>
      ) : users.length === 0 ? (
        <p className="text-ct-text-muted">Пользователи не найдены</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-ct-border bg-ct-surface shadow-ct-sm">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-gradient-to-r from-ct-primary to-ct-secondary text-ct-on-primary">
                <th className="px-4 py-3 font-semibold text-ct-on-primary/90">ID</th>
                <th className="px-4 py-3 font-semibold text-ct-on-primary/90">Логин</th>
                <th className="px-4 py-3 font-semibold text-ct-on-primary/90">Роль</th>
                <th className="px-4 py-3 font-semibold text-ct-on-primary/90">Создан</th>
                <th className="px-4 py-3 font-semibold text-ct-on-primary/90 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-ct-border last:border-0 hover:bg-indigo-50/40 transition-colors duration-150">
                  <td className="px-4 py-3 text-ct-text-muted">{u.id}</td>
                  <td className="px-4 py-3 font-semibold text-ct-text">{u.username}</td>
                  <td className="px-4 py-3 text-ct-text">{ROLE_LABELS[u.role] || u.role}</td>
                  <td className="px-4 py-3 text-ct-text-muted text-xs">{u.created_at}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-3">
                      <button
                        onClick={() => openEdit(u)}
                        className="text-ct-primary hover:text-ct-primary/80 text-sm font-semibold transition-colors"
                      >
                        Редакт.
                      </button>
                      <button
                        onClick={() => setDeleteTarget(u.id)}
                        className="text-red-400 hover:text-red-600 text-sm transition-colors"
                      >
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="w-full max-w-md rounded-xl bg-ct-surface border border-ct-border shadow-ct-md p-6">
            <h3 className="mb-4 text-lg font-bold text-ct-text">
              {editTarget ? 'Редактировать пользователя' : 'Создать пользователя'}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-semibold text-ct-text">Логин *</label>
                <input
                  type="text"
                  value={formUsername}
                  onChange={(e) => setFormUsername(e.target.value)}
                  className={inputStyle}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-ct-text">
                  Пароль {editTarget ? '(оставьте пустым, чтобы не менять)' : '*'}
                </label>
                <input
                  type="password"
                  value={formPassword}
                  onChange={(e) => setFormPassword(e.target.value)}
                  className={inputStyle}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-ct-text">Роль *</label>
                <select
                  value={formRole}
                  onChange={(e) => setFormRole(e.target.value)}
                  className={inputStyle}
                >
                  <option value="admin">Администратор</option>
                  <option value="operator">Оператор</option>
                  <option value="monitoring">Мониторинг</option>
                  <option value="accountant">Бухгалтер</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg border border-ct-border bg-ct-surface px-5 py-2 text-sm font-semibold text-ct-text-muted transition-all duration-200 hover:bg-indigo-50 hover:text-ct-primary active:scale-[0.98]"
              >
                Отмена
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formUsername.trim() || (!editTarget && !formPassword.trim())}
                className="rounded-full bg-gradient-to-r from-ct-primary to-ct-secondary px-5 py-2 text-sm font-bold text-ct-on-primary shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98] disabled:opacity-50"
              >
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Удаление пользователя"
        message="Вы уверены, что хотите удалить этого пользователя?"
        confirmLabel="Удалить"
        cancelLabel="Отмена"
        variant="danger"
        onConfirm={() => {
          if (deleteTarget !== null) {
            handleDelete(deleteTarget);
          }
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
